import { componentText, SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { sound } from '@schmancy/audio'
import { $notify } from '@schmancy/notification'
import { css, html, nothing } from 'lit'
import { property, state } from 'lit/decorators.js'
import { when } from 'lit/directives/when.js'
import { Subject, catchError, defer, exhaustMap, map, of, startWith, switchMap, takeUntil, tap, timer } from 'rxjs'
import '../button/button'
import '../button/icon-button'
import '../icons/icon'
import '../visually-hidden/visually-hidden'
import type { ButtonColor, ButtonVariant } from '../button/button'
import type { SchmancyButtonSize } from '../button/context'

/** How long the control shows that it worked before returning to rest. */
const CONFIRMATION_MS = 2000

export type SchmancyCopiedEvent = CustomEvent<{ value: string }>

/**
 * A control that puts a value on the clipboard and says what happened — the two
 * halves that were being hand-rolled together at every call site, where the
 * failure sentence was free to disagree with the one next to it.
 *
 * Disagreement is the whole reason this is a primitive rather than a snippet. A
 * value worth a copy button is usually a value that cannot be checked by eye —
 * a DNS record, an API key, a verification token — and one that is mistyped
 * fails silently, days later, with nothing pointing back at the copy that never
 * happened. So the failure path is the part that must be identical everywhere,
 * and it is the part a snippet loses first.
 *
 * ```html
 * <schmancy-copy .value=${record} subject="TXT record"></schmancy-copy>
 * <schmancy-copy .value=${key} subject="API key" label="Copy key"></schmancy-copy>
 * <schmancy-copy value="309,09" subject="KZ 66"><strong>309,09</strong></schmancy-copy>
 * ```
 *
 * @slot - Non-interactive content that becomes the copy trigger itself. Its
 * typography stays visible during confirmation; no copy icon is added.
 *
 * Success is announced where it happened: the control itself confirms, and a
 * live region says so once. It is deliberately NOT a toast — a settings page
 * carrying five of these would stack five toasts for five confirmations nobody
 * needs, and a toast reports from the corner of the screen about something the
 * eye is already resting on.
 *
 * Failure IS a toast, because it has to interrupt: the member is about to paste
 * something they do not have. The toast carries the value itself, so the
 * clipboard being unreachable — an insecure origin, a denied permission, a
 * webview with no clipboard at all — still leaves the thing on screen to
 * transcribe.
 *
 * @fires copied      - The clipboard write resolved; never on a refused one  { value }
 * @fires copy-failed - The write was refused or the clipboard is absent; detail is the reason
 */
@schmancyElement('schmancy-copy')
export class SchmancyCopy extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: inline-flex;
				flex-shrink: 0;
			}
		`,
	]

	/** The text put on the clipboard, verbatim. */
	@property({ type: String }) value = ''

	/**
	 * What is being copied — "TXT record", "API key", "invoice number". It is the
	 * control's name ("Copy TXT record"), so five copy controls on one page answer
	 * to five names instead of five `Copy`s, and it is the subject of both the
	 * confirmation and the failure sentence.
	 */
	@property({ type: String }) subject?: string

	/**
	 * Visible text for an empty default slot. With a label it is a labelled
	 * button, without one an icon button. Slotted content becomes the trigger
	 * itself and retains its presentation instead.
	 */
	@property({ type: String }) label?: string

	@property({ reflect: true, type: String }) variant: ButtonVariant = 'text'

	@property({ reflect: true, type: String }) color?: ButtonColor

	@property({ type: String }) size: SchmancyButtonSize = 'md'

	/** Inline feedback reserves a check position inside clipped or dense content. */
	@property({ type: String }) feedback: 'caption' | 'inline' = 'caption'

	@property({ type: Boolean, reflect: true }) disabled = false

	/** Play success feedback after the clipboard write; the shared mute preference applies. */
	@property({ type: Boolean, attribute: 'play-sound' }) playSound = false

	@state() private confirmed = false
	@state() private hasContent = false

	private readonly presses = new Subject<string>()

	connectedCallback() {
		super.connectedCallback()
		this.hasContent = Array.from(this.childNodes).some(
			node =>
				node.nodeType === Node.ELEMENT_NODE || (node.nodeType === Node.TEXT_NODE && Boolean(node.textContent?.trim())),
		)

		this.presses
			.pipe(
				// A second press while the write is still in flight is the same press:
				// the clipboard holds one value and it was asked for once.
				// The value is taken at the press, so a rebinding while the write is in
				// flight cannot make `copied` or the failure toast name a value that was
				// never asked for.
				exhaustMap(value =>
					defer(() =>
						// `navigator.clipboard` is absent outright on an insecure origin, so
						// the read of it belongs inside the deferred body with the write —
						// a throw raised while building this observable would escape onto
						// the subscription below and end it for the rest of the mount.
						navigator.clipboard
							? navigator.clipboard.writeText(value)
							: Promise.reject(new Error('The clipboard is unavailable on this origin')),
					).pipe(
						tap(() => {
							this.dispatchEvent(
								new CustomEvent<SchmancyCopiedEvent['detail']>('copied', {
									detail: { value },
									bubbles: true,
									composed: true,
								}),
							)
							if (this.playSound && !sound.muted) {
								try {
									sound.play('content')
								} catch {
									/* Sound availability cannot change a successful clipboard write. */
								}
							}
						}),
						map(() => true),
						catchError(error => {
							// Not `error`: a bubbling, composed `error` reaches `window`, where
							// every global error listener reads it as an uncaught exception.
							this.dispatchEvent(new CustomEvent('copy-failed', { detail: error, bubbles: true, composed: true }))
							$notify.error(
								this.subject
									? componentText('copy.failed', { subject: this.subject, value })
									: componentText('copy.failedUnnamed', { value }),
							)
							return of(false)
						}),
					),
				),
				// A fresh copy restarts the confirmation window rather than inheriting
				// the tail of the previous one.
				switchMap(copied =>
					copied
						? timer(CONFIRMATION_MS).pipe(
								map(() => false),
								startWith(true),
							)
						: of(false),
				),
				tap(confirmed => {
					this.confirmed = confirmed
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe()
	}

	protected firstUpdated() {
		if (import.meta.env.DEV && !this.subject) {
			console.warn(
				'[schmancy] <schmancy-copy> has no `subject`, so it announces as "Copy" — indistinguishable from every other copy control on the page, and its failure message cannot say what failed to copy. Give it the noun it acts on.',
				this,
			)
		}
	}

	private readonly press = () => {
		if (!this.disabled) this.presses.next(this.value)
	}

	protected render() {
		const name = this.subject
			? componentText('copy.action', { subject: this.subject })
			: componentText('copy.actionUnnamed')
		const icon = this.confirmed ? 'check' : 'content_copy'

		return html`
			<div class="relative" ?hidden=${!this.hasContent}>
				<schmancy-button
					variant="plain"
					justify="start"
					?disabled=${this.disabled}
					aria-label=${name}
					@click=${this.press}
				>
					<slot
						@slotchange=${(event: Event) => {
							if (event.currentTarget instanceof HTMLSlotElement) {
								this.hasContent = event.currentTarget
									.assignedNodes({ flatten: true })
									.some(
										node =>
											node.nodeType === Node.ELEMENT_NODE ||
											(node.nodeType === Node.TEXT_NODE && Boolean(node.textContent?.trim())),
									)
							}
						}}
					></slot>
					${when(this.feedback === 'inline', () => html`<schmancy-icon slot="suffix" aria-hidden="true" class=${this.confirmed ? 'opacity-100' : 'opacity-0'}>check</schmancy-icon>`)}
				</schmancy-button>
				${when(
					this.confirmed && this.feedback === 'caption',
					() => html`
						<span
							aria-hidden="true"
							class="pointer-events-none absolute bottom-full right-0 mb-1 rounded-xs bg-primary-container px-2 py-1 text-xs font-medium text-primary-onContainer"
						>
							${componentText('copy.done')}
						</span>
					`,
				)}
			</div>
			${when(!this.hasContent, () =>
				when(
					this.label,
					() => html`
						<schmancy-button
							.variant=${this.variant}
							.color=${this.color}
							.size=${this.size}
							?disabled=${this.disabled}
							aria-label=${name}
							@click=${this.press}
						>
							<schmancy-icon slot="prefix">${icon}</schmancy-icon>
							${this.confirmed ? componentText('copy.done') : this.label}
						</schmancy-button>
					`,
					() => html`
						<schmancy-icon-button
							.variant=${this.variant}
							.color=${this.color}
							.size=${this.size}
							?disabled=${this.disabled}
							.icon=${icon}
							aria-label=${name}
							@click=${this.press}
						></schmancy-icon-button>
					`,
				),
			)}

			<!--
				One region, rendered from the start so the announcement lands in a
				region assistive tech is already watching. It empties again when the
				confirmation does, which is what gives the next copy of the same value
				a change to announce.
			-->
			<schmancy-visually-hidden role="status" aria-live="polite">
				${
					this.confirmed
						? this.subject
							? componentText('copy.announce', { subject: this.subject })
							: componentText('copy.announceUnnamed')
						: nothing
				}
			</schmancy-visually-hidden>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-copy': SchmancyCopy
	}
}
